import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ShieldCheck, Clock, Award, ArrowRight, BookOpen, Sparkles, Briefcase, Brain, ClipboardCheck, Building2, TrendingDown, Users } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import LegalDisclaimer from "@/components/LegalDisclaimer";

const HERO_IMG = "https://images.pexels.com/photos/37087711/pexels-photo-37087711.jpeg";
const CTX_IMG = "https://images.pexels.com/photos/15831822/pexels-photo-15831822.jpeg";
const PPE_IMG = "https://images.pexels.com/photos/978808/pexels-photo-978808.jpeg";

const features = [
  { icon: BookOpen, title: "13 detailed modules", body: "OSHA-aligned lessons with statistics, procedures, and callouts covering HBV, HCV, HIV, PPE, and universal precautions." },
  { icon: Sparkles, title: "AI-graded case study", body: "Your final exam includes a profession-tailored case study graded by Claude Sonnet 4.5 with actionable feedback." },
  { icon: Briefcase, title: "10 AI practice cases", body: "Module 13 generates 10 fresh workplace scenarios matched to your profession — practice as often as you want." },
  { icon: Award, title: "Downloadable PDF certificate", body: "Score 80% or higher and instantly receive a 2-year valid certificate." },
];

const audiences = [
  "First responders & EMTs",
  "Healthcare workers",
  "Tattoo & piercing artists",
  "Custodial & janitorial staff",
  "Lab technicians",
  "Teachers & childcare workers",
  "Security & correctional staff",
  "Anyone handling human blood",
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const primaryCta = user ? "/dashboard" : "/register";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-200">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/70 to-white" />
          <div className="noise-overlay" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-in">
              <div className="inline-flex items-center gap-3 border border-zinc-900 bg-white px-3 py-1.5 rounded-full mb-6">
                <img src="/imcac_logo.png" alt="IMCAC" className="h-5 w-5 object-contain" />
                <span className="text-xs font-mono uppercase tracking-widest text-zinc-800">IMCAC · Department of Occupational Safety Programs</span>
              </div>

              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-zinc-900">
                International Medical<br />
                Competency<br />
                Accreditation Council<br />
                <span className="text-red-800">Bloodborne Pathogens Training</span>
              </h1>

              <div className="mt-6 text-sm font-mono text-zinc-600 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-bold text-zinc-900">IMCAC</span>
                <span className="text-zinc-400">→</span>
                <span>International Medical Competency Accreditation Council</span>
                <span className="text-zinc-400">·</span>
                <a href="https://imcac.org" target="_blank" rel="noopener noreferrer" className="text-red-800 hover:underline">
                  imcac.org
                </a>
              </div>
              <p className="mt-1 text-xs font-mono text-zinc-500 uppercase tracking-widest">
                Department of Occupational Safety Programs · Section 8 non-profit · Companies Act, 2013, India
              </p>

              <p className="mt-8 text-lg text-zinc-700 max-w-xl leading-relaxed">
                A rigorous, OSHA-aligned online competency assessment from <strong>IMCAC — the International Medical Competency Accreditation Council, an independent competency assessment and certification body</strong>. Covering HBV, HCV, HIV, universal precautions, PPE, and exposure response, with an AI tutor on standby, an AI-graded case study, and a downloadable competency certificate valid for two calendar years.
                <span className="block mt-2 text-sm text-zinc-500">
                  A <strong>competency-based credential</strong> — it does <em>not</em> replace, supersede, or constitute professional registration, licensure, or authorization to practice under the laws of any country.
                </span>
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Button
                  data-testid="hero-cta-start"
                  onClick={() => navigate(primaryCta)}
                  className="bg-red-600 hover:bg-red-700 text-white px-8 h-12 text-base font-medium"
                >
                  Start certification <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Link
                  to={user ? "/course" : "/login"}
                  data-testid="hero-cta-secondary"
                  className="text-sm font-medium underline decoration-red-600 decoration-2 underline-offset-4 hover:text-red-600"
                >
                  {user ? "Continue where I left off →" : "I already have an account →"}
                </Link>
                <Link
                  to="/corporate"
                  data-testid="hero-cta-teams"
                  className="text-sm font-medium underline decoration-zinc-500 decoration-2 underline-offset-4 hover:text-zinc-900"
                >
                  Certify a team →
                </Link>
              </div>

              <div className="mt-10 flex flex-wrap gap-6 text-sm text-zinc-600">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-red-600" /> No credit card required to try</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-red-600" /> Mobile & desktop friendly</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-red-600" /> 2-year certificate</div>
              </div>
            </div>

            <div className="lg:col-span-5 fade-in">
              <div className="relative">
                <div className="absolute -inset-2 bg-red-600/10 rounded-lg" />
                <div className="relative bg-white border-2 border-zinc-900 rounded-lg overflow-hidden shadow-xl">
                  <img src={CTX_IMG} alt="Scientist in PPE" className="w-full h-72 object-cover" />
                  <div className="p-6 border-t-2 border-zinc-900 bg-white">
                    <div className="flex items-baseline justify-between mb-3">
                      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Certification</div>
                      <div className="text-xs font-mono text-red-800">IMCAC · 13 MODULES · 50-Q + AI CASE STUDY</div>
                    </div>
                    <div className="flex items-start gap-3">
                      <img src="/imcac_logo.png" alt="" className="h-12 w-12 object-contain flex-shrink-0" />
                      <div className="font-display text-xl font-bold leading-tight">
                        Bloodborne Pathogens Training & Certification
                      </div>
                    </div>
                    <div className="mt-4 h-1 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="h-full w-4/5 bg-red-800" />
                    </div>
                    <div className="mt-2 text-xs text-zinc-500">IMCAC · Department of Occupational Safety Programs · 80% pass score</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-2xl mb-16">
            <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-4">What's inside</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">
              Everything you need to certify — and actually learn something.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 border-t border-l border-zinc-200">
            {features.map((f, i) => (
              <div key={i} className="border-r border-b border-zinc-200 p-8 hover:bg-zinc-50 transition-colors" data-testid={`feature-card-${i}`}>
                <f.icon className="h-6 w-6 text-red-600 mb-6" strokeWidth={1.5} />
                <div className="font-display font-bold text-lg mb-2">{f.title}</div>
                <div className="text-sm text-zinc-600 leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI CASE STUDY HIGHLIGHT */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-testid="landing-ai-case-study-section">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 border border-red-800 bg-white px-3 py-1.5 rounded-full mb-6">
                <Sparkles className="h-3.5 w-3.5 text-red-800" />
                <span className="text-xs font-mono uppercase tracking-widest text-red-800">
                  Powered by Claude Sonnet 4.5
                </span>
              </div>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                Real workplace scenarios.<br />
                <span className="text-red-800">Graded by AI.</span>
              </h2>
              <p className="text-zinc-700 mt-6 leading-relaxed max-w-lg">
                This isn't just multiple choice. Your certification exam includes an <strong>AI-graded case study tailored to your profession</strong> — and Module 13 gives you unlimited practice with fresh scenarios every time.
              </p>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-red-800 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-zinc-700">
                    <strong>Profession-specific scenarios</strong> — Nurse, phlebotomist, tattoo artist, custodial staff, EMT, and more. Cases feel real because they match your job.
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-red-800 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-zinc-700">
                    <strong>Actionable feedback</strong> — Claude 4.5 grades your response against OSHA 29 CFR 1910.1030 and tells you exactly what you missed.
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-red-800 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-zinc-700">
                    <strong>Practice unlimited</strong> — Module 13 generates 10 fresh case studies with 4 answer choices + explanations each session.
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-5">
              {/* Exam case study card */}
              <div className="bg-white border-2 border-zinc-900 rounded-lg p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="inline-flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4 text-red-800" />
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-900 font-bold">
                      Final Exam · AI Case Study
                    </span>
                  </div>
                  <span className="text-xs font-mono bg-red-50 text-red-800 px-2 py-1 rounded">30% of score</span>
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Sample scenario · Phlebotomist</div>
                <p className="text-sm text-zinc-800 leading-relaxed border-l-4 border-red-800 bg-zinc-50 rounded-r-md p-4">
                  <em>&ldquo;During a routine morning blood draw, you experience an accidental needlestick from a patient whose HIV/HBV status is unknown. Walk through your complete response — PPE, immediate first-aid, source-patient consent, medical eval timing, PEP considerations, and documentation.&rdquo;</em>
                </p>
                <div className="mt-4 flex items-center gap-2 text-xs text-zinc-500 font-mono">
                  <Brain className="h-3.5 w-3.5 text-red-800" /> AI grades on OSHA compliance, PPE, timing, and documentation.
                </div>
              </div>

              {/* Module 13 practice card */}
              <div className="bg-zinc-900 text-white rounded-lg p-6 border-2 border-zinc-900">
                <div className="flex items-center justify-between mb-4">
                  <div className="inline-flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-red-400" />
                    <span className="text-xs font-mono uppercase tracking-widest font-bold">
                      Module 13 · AI Practice Cases
                    </span>
                  </div>
                  <span className="text-xs font-mono bg-red-950 text-red-300 px-2 py-1 rounded">Unlimited</span>
                </div>
                <div className="font-display text-2xl font-bold leading-tight">
                  10 fresh cases. 4 answer choices each.<br />
                  <span className="text-red-400">Tailored to your job.</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["Registered Nurse", "EMT", "Tattoo Artist", "School Nurse", "Custodial Staff", "Lab Tech", "Firefighter"].map((p) => (
                    <span key={p} className="text-[10px] font-mono uppercase tracking-widest bg-zinc-800 border border-zinc-700 px-2 py-1 rounded">{p}</span>
                  ))}
                  <span className="text-[10px] font-mono uppercase tracking-widest bg-red-950 border border-red-900 text-red-300 px-2 py-1 rounded">+ your own</span>
                </div>
                <div className="mt-4 text-xs text-zinc-400 leading-relaxed">
                  Each session generates a completely new set of scenarios with instant answer explanations. Scores are tracked on your dashboard.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-4">Who needs this</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-8">
              If your work brings you near blood, this is for you.
            </h2>
            <div className="grid grid-cols-2 gap-y-3 gap-x-8 text-zinc-700">
              {audiences.map((a) => (
                <div key={a} className="flex items-start gap-2 text-sm">
                  <ShieldCheck className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <span>{a}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="border-2 border-zinc-900 overflow-hidden rounded-lg">
              <img src={PPE_IMG} alt="Safety equipment" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-red-600 text-white p-6 max-w-xs border-2 border-zinc-900 rounded-lg">
              <div className="text-xs font-mono uppercase tracking-widest opacity-80">Certificate valid for</div>
              <div className="font-display font-black text-5xl leading-none mt-1">2 yrs</div>
              <div className="text-xs mt-2 opacity-90">Per OSHA training standards.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT IMCAC */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-4">About the assessment body</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">
              What does<br />IMCAC stand for?
            </h2>
            <div className="mt-8 border-l-4 border-red-800 pl-5">
              <div className="font-display font-bold text-2xl leading-tight">
                International Medical Competency Accreditation Council
              </div>
              <a
                href="https://imcac.org"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-block text-sm font-mono text-red-800 hover:underline"
                data-testid="landing-imcac-website-link"
              >
                imcac.org →
              </a>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-5 text-zinc-700 leading-relaxed">
            <p>
              <strong>IMCAC — the International Medical Competency Accreditation Council —</strong> and its department, the <strong>Department of Occupational Safety Programs (DOOSP)</strong>, operate as an <strong>independent competency assessment and certification body</strong>. We design and award competency-based credentials for medical, health, and safety professionals.
            </p>
            <p>
              DOOSP is the arm of IMCAC that runs OSHA-aligned workplace-safety competency assessments — including the Bloodborne Pathogens Training you are taking here. Each certificate reflects successful completion of a competency-based assessment aligned to the topics of <strong>OSHA 29 CFR 1910.1030</strong>, and is valid for two calendar years from the date of issue.
            </p>
            <p>
              IMCAC/DOOSP certifications <strong>do not replace, supersede, or constitute professional registration, licensure, or authorization to practice</strong> under the laws and regulations of any country or jurisdiction. IMCAC is not a licensing authority or regulator, and is not accredited by or affiliated with OSHA, the CDC, the WHO, or any government body. IMCAC is a registered <strong>Section 8 company under the Companies Act, 2013, India</strong>, operating as a not-for-profit organisation. For institutional inquiries or verification support, visit <a href="https://imcac.org" target="_blank" rel="noopener noreferrer" className="text-red-800 font-semibold hover:underline">imcac.org</a>.
            </p>
          </div>
        </div>
      </section>

      {/* FOR TEAMS — Volume pricing */}
      <section className="border-b border-zinc-200 bg-zinc-900 text-white" data-testid="landing-teams-section">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 border border-red-500 bg-red-950 px-3 py-1.5 rounded-full mb-6">
                <Building2 className="h-3.5 w-3.5 text-red-400" />
                <span className="text-xs font-mono uppercase tracking-widest text-red-300">For teams &amp; employers</span>
              </div>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                Certifying a team?<br />
                <span className="text-red-400">Save up to 33% at 50 seats.</span>
              </h2>
              <p className="text-zinc-400 mt-6 leading-relaxed max-w-lg">
                Order 1–500 IMCAC Bloodborne Pathogens certificates in one payment. Every seat is
                issued instantly with a downloadable roster, individual PDFs, and shareable claim
                links for your employees.
              </p>
              <div className="mt-8 space-y-3">
                <div className="flex items-start gap-3">
                  <TrendingDown className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-zinc-300"><strong className="text-white">Automatic volume discount</strong> — the more seats, the lower the per-seat price.</div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-zinc-300"><strong className="text-white">One payment, N certificates</strong> — pay with PayPal or request an invoice (Net 15).</div>
                </div>
                <div className="flex items-start gap-3">
                  <Award className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-zinc-300"><strong className="text-white">Downloadable roster + ZIP</strong> — grab all PDFs at once or share individual claim links.</div>
                </div>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/corporate"
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white h-11 px-6 rounded-md text-sm font-medium"
                  data-testid="landing-teams-cta"
                >
                  Order for your team <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="mailto:admin@imcac.org" className="text-sm text-zinc-400 hover:text-white underline decoration-red-500 decoration-2 underline-offset-4">
                  Talk to us about custom pricing →
                </a>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "1–9 seats", price: 30, save: 0, popular: false },
                  { label: "10–24 seats", price: 25, save: 17, popular: true },
                  { label: "25–49 seats", price: 22, save: 27, popular: false },
                  { label: "50+ seats", price: 20, save: 33, popular: false },
                ].map((t) => (
                  <div
                    key={t.label}
                    className={`rounded-lg p-5 relative ${t.popular ? "bg-red-950 border-2 border-red-500" : "bg-zinc-800 border border-zinc-700"}`}
                  >
                    {t.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-500 text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded">
                        Popular
                      </div>
                    )}
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{t.label}</div>
                    <div className="font-display text-4xl font-black mt-2">${t.price}</div>
                    <div className="text-[11px] text-zinc-400 font-mono">per seat</div>
                    {t.save > 0 && (
                      <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-mono uppercase text-emerald-400">
                        <TrendingDown className="h-3 w-3" /> save {t.save}%
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-8 grid grid-cols-3 gap-6 pt-6 border-t border-zinc-800">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Sample</div>
                  <div className="mt-2 font-display text-2xl font-bold">10 seats</div>
                  <div className="text-sm text-zinc-400 mt-1">$250 total · <span className="text-emerald-400">$50 saved</span></div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Sample</div>
                  <div className="mt-2 font-display text-2xl font-bold">25 seats</div>
                  <div className="text-sm text-zinc-400 mt-1">$550 total · <span className="text-emerald-400">$200 saved</span></div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Sample</div>
                  <div className="mt-2 font-display text-2xl font-bold">100 seats</div>
                  <div className="text-sm text-zinc-400 mt-1">$2,000 total · <span className="text-emerald-400">$1,000 saved</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SAMPLE CERTIFICATE SHOWCASE */}
      <section className="border-b border-zinc-200 bg-white" data-testid="landing-sample-cert-section">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-4">What you take home</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">
              Your IMCAC certificate<br />
              looks like this.
            </h2>
            <p className="text-zinc-600 mt-5 leading-relaxed">
              Print-ready landscape PDF, signed by the Director of the Department of Occupational Safety Programs, with a QR code that any employer can scan to verify authenticity in real time.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 bg-red-800/5 rounded-lg" />
            <div className="relative border-2 border-zinc-900 rounded-lg overflow-hidden shadow-2xl bg-white">
              <img
                src="/sample_certificate.jpg"
                alt="Sample IMCAC certificate preview"
                className="w-full h-auto block"
                data-testid="landing-sample-cert-image"
              />
            </div>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            <div className="border-l-4 border-red-800 pl-4">
              <div className="font-display font-bold">Print-ready PDF</div>
              <div className="text-sm text-zinc-600 mt-1">Landscape US letter, high resolution.</div>
            </div>
            <div className="border-l-4 border-red-800 pl-4">
              <div className="font-display font-bold">QR verification</div>
              <div className="text-sm text-zinc-600 mt-1">Scan on any phone → public verify page.</div>
            </div>
            <div className="border-l-4 border-red-800 pl-4">
              <div className="font-display font-bold">Valid 2 years</div>
              <div className="text-sm text-zinc-600 mt-1">Recertification reminder built in.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-zinc-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="font-display text-4xl lg:text-6xl font-black tracking-tight">
            Get IMCAC-certified today.<br /><span className="text-red-500">Protect yourself tomorrow.</span>
          </h2>
          <p className="mt-6 text-zinc-400 max-w-xl mx-auto">
            Join thousands of professionals who trust IMCAC — an independent competency assessment and certification body — for their OSHA-aligned Bloodborne Pathogens Training.
          </p>
          <div className="mt-10">
            <Button
              data-testid="cta-footer-start"
              onClick={() => navigate(primaryCta)}
              className="bg-red-600 hover:bg-red-700 text-white h-12 px-8 text-base"
            >
              Begin your training <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-800 bg-zinc-900 text-zinc-500 py-10 text-xs font-mono">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <img src="/imcac_logo.png" alt="IMCAC" className="h-8 w-8 object-contain opacity-90" />
              <span className="uppercase tracking-widest text-zinc-300">
                International Medical Competency Accreditation Council (IMCAC)
              </span>
            </div>
            <div className="text-zinc-400 mb-3">Department of Occupational Safety Programs</div>
            <a
              href="https://imcac.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-300 hover:text-white underline decoration-red-800 underline-offset-4"
              data-testid="footer-imcac-website-link"
            >
              imcac.org
            </a>
            <div className="mt-4 text-zinc-500">
              © {new Date().getFullYear()} IMCAC · Independent competency assessment aligned to OSHA 29 CFR 1910.1030
            </div>
            <div className="mt-3 text-zinc-400 flex items-center justify-center gap-4 text-[11px]">
              <Link to="/terms" className="hover:text-white underline decoration-zinc-600 underline-offset-4" data-testid="landing-footer-terms">
                Terms
              </Link>
              <span className="text-zinc-700">·</span>
              <Link to="/privacy" className="hover:text-white underline decoration-zinc-600 underline-offset-4" data-testid="landing-footer-privacy">
                Privacy
              </Link>
              <span className="text-zinc-700">·</span>
              <Link to="/verify" className="hover:text-white underline decoration-zinc-600 underline-offset-4">
                Verify a certificate
              </Link>
            </div>
          </div>

          <LegalDisclaimer variant="footer" className="mt-8 pt-6 border-t border-zinc-800" />
        </div>
      </footer>
    </div>
  );
}
